import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Search, Building2, UserCircle, Menu, LifeBuoy } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import NotificationsBell from "@/components/common/Notifications";
import LocaleThemeSwitch from "@/components/common/LocaleThemeSwitch";
import { useSidebar } from "./SidebarContext";
import { useT } from "@/i18n/I18nContext";

const SEGMENT_LABELS = {
  "": "Dashboard",
  users: "User Management",
  patients: "Patient Management",
  facilities: "Facility Management",
  roles: "Roles & Permissions",
  lookups: "Lookups",
  audit: "Audit Trail",
  onboarding: "Onboarding",
  requests: "Requests",
  new: "New Request",
  edit: "Edit",
  view: "View",
};

export default function TopHeader() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("all");

  useEffect(() => {
    api
      .get("/facilities", { params: { page_size: 50 } })
      .then((res) => setFacilities(res.data.items || []))
      .catch(() => {});
  }, []);

  const segments = location.pathname.split("/").filter(Boolean);
  const crumbs = [{ label: t("Home"), to: "/" }];
  let acc = "";
  segments.forEach((seg) => {
    acc += "/" + seg;
    const raw = SEGMENT_LABELS[seg] || seg;
    const label = SEGMENT_LABELS[seg] ? t(raw) : raw;
    crumbs.push({ label, to: acc });
  });

  const initials = user
    ? `${(user.first_name || "U")[0]}${(user.last_name || "")[0]}`.toUpperCase()
    : "U";
  const { collapsed, openMobile } = useSidebar();
  const currentLabel = crumbs[crumbs.length - 1]?.label || "Home";

  return (
    <header
      data-testid="top-header"
      className={`fixed top-0 right-0 left-0 ${collapsed ? "lg:left-16" : "lg:left-64"} z-20 h-16 bg-white border-b border-[#E5E7EB] flex items-center px-3 sm:px-6 gap-2 sm:gap-4 transition-all duration-200`}
    >
      {/* Mobile menu button */}
      <button
        type="button"
        onClick={openMobile}
        data-testid="mobile-menu-button"
        aria-label="Open menu"
        className="lg:hidden text-[#1F2937] p-1.5 -ml-1 rounded hover:bg-[#F5F7FA] shrink-0"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile page title */}
      <div className="sm:hidden text-sm font-semibold text-[#1F2937] truncate min-w-0" data-testid="mobile-page-title">
        {currentLabel}
      </div>

      {/* Breadcrumb (tablet/desktop) */}
      <div data-testid="breadcrumb" className="hidden sm:flex items-center gap-1 text-sm text-[#6B7280] min-w-0 overflow-hidden">
        {crumbs.map((c, i) => (
          <React.Fragment key={c.to + i}>
            {i > 0 && <span className="text-[#94A3B8]">/</span>}
            <button
              data-testid={`breadcrumb-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => navigate(c.to)}
              className={`hover:text-[#0B7E8C] truncate ${
                i === crumbs.length - 1 ? "text-[#1F2937] font-medium" : ""
              }`}
            >
              {c.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="flex-1" />

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg px-3 py-1.5 w-72">
        <Search className="w-4 h-4 text-[#94A3B8]" />
        <input
          data-testid="global-search"
          placeholder={t("Search patients, users, facilities…")}
          className="bg-transparent outline-none text-sm text-[#1F2937] placeholder-[#94A3B8] w-full"
        />
      </div>

      {/* Facility selector */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="facility-selector"
            className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-lg border border-[#E5E7EB] text-sm text-[#1F2937] hover:bg-[#F5F7FA] shrink-0"
          >
            <Building2 className="w-4 h-4 text-[#0B7E8C]" />
            <span className="hidden sm:inline max-w-[160px] truncate">
              {selectedFacility === "all"
                ? t("All Facilities")
                : facilities.find((f) => f.id === selectedFacility)?.name || t("Select")}
            </span>
            <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>{t("Facility scope")}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setSelectedFacility("all")} data-testid="facility-option-all">
            {t("All Facilities")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {facilities.map((f) => (
            <DropdownMenuItem
              key={f.id}
              data-testid={`facility-option-${f.code}`}
              onClick={() => setSelectedFacility(f.id)}
            >
              {f.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Locale + Theme */}
      <LocaleThemeSwitch variant="header" />

      {/* Notifications */}
      <NotificationsBell />

      {/* User profile */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-profile-button"
            className="flex items-center gap-2 pl-2 pr-3 py-1 rounded-lg hover:bg-[#F5F7FA]"
          >
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-[#0B7E8C] text-white text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-left">
              <div className="text-sm font-medium text-[#1F2937] leading-tight">
                {user ? `${user.first_name} ${user.last_name}` : t("User")}
              </div>
              <div className="text-[11px] text-[#6B7280] capitalize">
                {user?.role_code || "user"}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="text-sm font-medium">{user?.email}</div>
            <div className="text-xs text-[#6B7280] capitalize">{user?.role_code}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="my-profile-button">
            <UserCircle className="w-4 h-4 mr-2" />
            {t("My Profile")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/support")} data-testid="profile-support-button">
            <LifeBuoy className="w-4 h-4 mr-2" />
            {t("Contact Support")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={logout} data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            {t("Sign out")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
